import Action from 'titus-ts/dist/js/Util/Actions/Action';
import PolicyEvaluationResult from 'titus-ts/dist/js/Util/Actions/PolicyEvaluationResult';
import { TimeOutService } from './timeOutService';
import DialogService from './dialogService';
import { localSettingsService } from './localSettingsService';

export class CustomActionService {
    private timeOutService: TimeOutService;
    private dialogService: DialogService;

    public constructor(timeOutService: TimeOutService, dialogService: DialogService) {
        this.timeOutService = timeOutService;
        this.dialogService = dialogService;
    }

    public processCustomActions = async (result: PolicyEvaluationResult): Promise<boolean> => {
        // if result is already blocked no point running the custom action
        if (result.status == 'BLOCKED') {
            return false;
        }

        // filter just the custom actions
        const customActions = result.actions.filter((action) => action.type == customActionTypeName);

        const customActionResults = await Promise.all(customActions.map((action) => this.processCustomAction(action)));

        const customActionFailures = customActionResults.filter((result) => !result.success);

        return this.processCustomActionFailures(customActionFailures);
    };

    private processCustomAction = async (customAction: Action): Promise<CustomActionResult> => {
        try {
            const timeout = localSettingsService.getLocalSettings().customActionTimeout;
            if (customAction.type != customActionTypeName) {
                throw 'action is wrong type';
            }

            const baseUrl = customAction.params.get(CustomActionParameterNames.Url);
            const additionalParameters = customAction.params.get(CustomActionParameterNames.Paramaters);

            if (!baseUrl) {
                throw 'no Url configured in action.';
            }

            const url = new URL(baseUrl);
            if (additionalParameters) {
                url.search = additionalParameters;
            }

            const response = await this.fetchWithTimeout(url, undefined, timeout);

            let responseBody: string | null = null;
            if (response.ok) {
                responseBody = await response.text();
            }

            return {
                success: response.ok,
                statusCode: response.status,
                responseBody: responseBody,
                action: customAction,
            } as CustomActionResult;
        } catch (error) {
            return {
                success: false,
                statusCode: 0,
                responseBody: null,
                action: customAction,
            } as CustomActionResult;
        }
    };

    private processCustomActionFailures = async (customActionFailures: CustomActionResult[]): Promise<boolean> => {
        for (const customActionFailure of customActionFailures) {
            const title = customActionFailure.action.params.get(CustomActionParameterNames.Title) || '';
            const errorMessage = customActionFailure.action.params.get(CustomActionParameterNames.Message) || '';
            const severity =
                customActionFailure.action.params.get(CustomActionParameterNames.Severity) ||
                CustomActionSeverityTypes.Warn;

            let dialogResult = true;
            if (severity == CustomActionSeverityTypes.LogOnly) {
                console.warn(`CustomAction Failed:`);
                console.group(title);
                console.info(`httpStatusCode: ${customActionFailure.statusCode}`);
                console.info(`message: ${errorMessage}`);
                console.groupEnd();
            } else {
                dialogResult =
                    (await this.dialogService.showCustomActionFailureDialog(title, errorMessage, severity)) ==
                    'DialogOk';
            }

            if (dialogResult == false) {
                return false;
            }
        }

        return true;
    };

    private fetchWithTimeout = (url: string | URL | Request, options: RequestInit = {}, timeoutMs: number) => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), timeoutMs);

        return fetch(url, {
            ...options,
            signal: controller.signal,
        }).finally(() => clearTimeout(timeout));
    };
}

type CustomActionResult = {
    success: boolean;
    statusCode: number;
    responseBody: string | null;
    action: Action;
};

export enum CustomActionSeverityTypes {
    LogOnly = 'Log only',
    Warn = 'Warn',
    Prevent = 'Prevent',
}

export enum CustomActionParameterNames {
    Url = 'CustomActionName',
    Paramaters = 'AdditionalParameters',
    Title = 'Title',
    Message = 'ErrorMessage',
    Severity = 'Severity',
}

export const customActionTypeName = 'CustomAction';
